import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NewsCard } from "@/components/NewsCard";
import { useAuth } from "@/context/AuthContext";
import { api, BOAT_CATEGORIES } from "@/lib/api";
import { Section, LandingNav, Hero, IscrivitiCta, Contatti, LandingFooter } from "@/components/landing/LandingParts";
import { ClasseCard, RegataRow, RankingClasse, CalendarioLink } from "@/components/landing/LandingData";

const useLandingData = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    Promise.all(["/associazione", "/public/classi", "/public/regate", "/public/ranking", "/public/notizie"].map((u) => api.get(u).then((r) => r.data)))
      .then(([assoc, classi, regate, ranking, news]) => setD({ assoc, classi, regate, ranking, news }))
      .catch(() => setErr(true));
  }, []);
  return { d, err };
};

export default function Landing() {
  const { user } = useAuth();
  const { d, err } = useLandingData();
  const logged = !!user;
  if (err) {
    return (
      <div className="min-h-screen bg-[#0B192C] text-white grid place-items-center p-6 text-center" data-testid="landing-error">
        <div>
          <p className="mb-4">Impossibile caricare la pagina. Riprova tra poco.</p>
          <button onClick={() => window.location.reload()} className="underline text-emerald-400" data-testid="landing-retry-button">Riprova</button>
        </div>
      </div>
    );
  }
  if (!d) return <div className="min-h-screen bg-[#0B192C]" />;
  const { assoc } = d;
  return (
    <div className="bg-[#F4F6F9]" data-testid="landing-page">
      <LandingNav nome={assoc.nome} logged={logged} hasRanking={d.ranking.length > 0} />
      <Hero nome={assoc.nome} logged={logged} classi={d.classi} />
      <Section id="chi-siamo" kicker="L'associazione" title="Chi siamo">
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10">
          <div className="text-slate-700 text-base sm:text-lg leading-relaxed whitespace-pre-line" data-testid="landing-chi-siamo-text">
            {assoc.chi_siamo || "Presentazione dell'associazione in arrivo."}
          </div>
          <Contatti d={assoc} />
        </div>
      </Section>
      <Section id="news" kicker="Dal mondo 9er e Nacra" title="Ultime news">
        {d.news.length === 0 ? (
          <p className="text-muted-foreground" data-testid="landing-news-empty">Nessuna news pubblicata.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="landing-news-list">
            {d.news.map((n, k) => <NewsCard key={n.id} n={n} delay={k * 80} publicBase="/associazione/news" />)}
          </div>
        )}
        {logged && <Link to="/notizie" className="inline-block mt-8 text-emerald-700 hover:underline font-heading uppercase tracking-wider" data-testid="landing-news-all-link">Tutte le news →</Link>}
      </Section>
      <Section id="classi" kicker="Le nostre barche" title="Le classi">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {d.classi.map((c) => <ClasseCard key={c.categoria} c={c} />)}
        </div>
      </Section>
      <section className="bg-emerald-600" data-testid="landing-section-iscriviti">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold uppercase text-white">Iscriviti ora</h2>
            <p className="text-white/85 mt-2 max-w-xl">Tesserati all'associazione: calendario regate, news, gestione delle tue barche e mercatino dell'usato.</p>
          </div>
          <div className="[&_button]:bg-[#0B192C] [&_button:hover]:bg-[#13294a]"><IscrivitiCta logged={logged} testid="cta-iscriviti-button" /></div>
        </div>
      </section>
      <Section id="regate" kicker="Calendario" title="Prossime regate" dark>
        {d.regate.length === 0 ? (
          <p className="text-white/60" data-testid="landing-regate-empty">Nessuna regata in programma.</p>
        ) : (
          <ul className="border-t border-white/10">{d.regate.map((r) => <RegataRow key={r.id} r={r} />)}</ul>
        )}
        <CalendarioLink logged={logged} />
      </Section>
      {d.ranking.length > 0 && (
      <Section id="ranking" kicker="Classifiche" title="Ranking list">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BOAT_CATEGORIES.filter((c) => d.ranking.some((r) => r.categoria === c)).map((c) => <RankingClasse key={c} categoria={c} items={d.ranking.filter((r) => r.categoria === c)} />)}
          </div>
      </Section>
      )}
      <LandingFooter d={assoc} />
    </div>
  );
}
